import React, { useState, useEffect } from "react";
import "./css/ManageTeamPage.css";
import logo from "./KICKS-6.png";
import { v4 as uuidv4 } from "uuid";
import { getCurrentUser } from "@aws-amplify/auth";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import {
  faTrash,
  faEdit,
  faCheck,
  faTimes,
  faArchive,
  faUndo,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import config from "./config";

const devLog = (...args) => {
  if (config.IS_DEV) {
    console.log(...args);
  }
};

const API_URL =
  "https://lykns00a87.execute-api.us-east-2.amazonaws.com/default/football_app_data_crud";

const ManageTeamPage = ({ userId: propUserId }) => {
  const [userId, setUserId] = useState(propUserId);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [players, setPlayers] = useState([]);
  const [archivedPlayers, setArchivedPlayers] = useState([]);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [editedPlayerName, setEditedPlayerName] = useState("");
  const [confirmDeletePlayer, setConfirmDeletePlayer] = useState(null);
  const [showArchivedPlayers, setShowArchivedPlayers] = useState(false);
  const navigate = useNavigate();
  const [preferencesVisible, setPreferencesVisible] = useState(true);

  const colorOptions = [
    { name: "Red", value: "#FF4C4C" },
    { name: "Blue", value: "#4C6FFF" },
    { name: "Green", value: "#4CFF4C" },
    { name: "Purple", value: "#B84CFF" },
    { name: "Orange", value: "#FF944C" },
    { name: "Yellow", value: "#FFD700" },
  ];

  const matchTypes = ["5", "6", "7", "9", "11"];
  const matchLengths = ["10", "12.5", "15", "20", "25", "30", "35", "40", "45"];

  const [matchType, setMatchType] = useState("5");
  const [matchLength, setMatchLength] = useState("10");
  const [numberOfParts, setNumberOfParts] = useState("2");
  const [markerColor, setMarkerColor] = useState(colorOptions[0].value);

  const handlePreferenceChange = async (preferenceType, value) => {
    try {
      devLog(`Updating preference: ${preferenceType} to ${value}`);

      const requestPayload = {
        operationType: "update_team_preference",
        data: {
          team_id: selectedTeam,
          preferences: {
            [preferenceType]: value,
          },
        },
      };

      const response = await axios.post(API_URL, requestPayload);

      if (response.status === 200) {
        devLog(`Preference updated successfully: ${preferenceType}`);
      } else {
        console.error(
          `Failed to update preference: ${preferenceType}`,
          response.statusText
        );
      }
    } catch (error) {
      console.error(
        `Error updating preference: ${preferenceType}`,
        error.message
      );
    }
  };

  const fetchTeamPreferences = async (teamId) => {
    try {
      const requestPayload = {
        operationType: "fetch_team_preference",
        data: { team_id: teamId },
      };

      const response = await axios.post(API_URL, requestPayload);
      const preferences = response.data.body
        ? JSON.parse(response.data.body).preferences
        : {};

      devLog("Fetched team preferences:", preferences);

      setMatchType(preferences.matchType || "5");
      setMatchLength(preferences.matchLength || "10");
      setNumberOfParts(preferences.numberOfParts || "2");
      setMarkerColor(preferences.markerColor || colorOptions[0].value);
    } catch (error) {
      console.error("Error fetching team preferences:", error.message);
      setError(
        "Failed to load team preferences. Please try again. If you just joined, please set them up below"
      );
    }
  };

  // Auto-clear messages after timeout
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fetch authenticated user ID if not passed via props
  useEffect(() => {
    if (!propUserId) {
      getCurrentUser()
        .then((user) => {
          const fetchedUserId = user?.attributes?.sub;
          if (fetchedUserId) {
            setUserId(fetchedUserId);
            devLog("Fetched userId:", fetchedUserId);
          } else {
            console.error("User ID not found.");
          }
        })
        .catch((error) => {
          console.error("Error fetching user from Auth:", error);
        });
    }
  }, [propUserId]);

  useEffect(() => {
    devLog("Players state:", players);
  }, [players]);

  useEffect(() => {
    if (selectedTeam) {
      fetchTeamPreferences(selectedTeam);
      fetchPlayers(selectedTeam);
    }
  }, [selectedTeam]);

  useEffect(() => {
    if (userId) {
      devLog("useEffect triggered: Fetching teams for userId:", userId);
      fetchTeams(userId);
    } else {
      devLog("useEffect triggered: userId not found.");
    }
  }, [userId]);

  const fetchTeams = async (managerId) => {
    devLog("Fetching teams for managerId:", managerId);
    setLoading(true);

    try {
      const requestPayload = {
        operationType: "fetch_teams_by_manager_id",
        data: { manager_id: managerId },
      };

      const response = await axios.post(`${API_URL}`, requestPayload);
      const teamsData = response.data.body
        ? JSON.parse(response.data.body).teams
        : [];
      devLog("Fetched team IDs:", teamsData);

      const detailedTeams = await Promise.all(
        teamsData.map(async (team) => {
          const team_name = await fetchTeamName(team.team_id);
          return { team_id: team.team_id, team_name };
        })
      );

      devLog("Detailed teams with names:", detailedTeams);
      setTeams(detailedTeams);

      if (detailedTeams.length === 1) {
        setSelectedTeam(detailedTeams[0].team_id);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      setError("Failed to load teams. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamName = async (teamId) => {
    try {
      const requestPayload = {
        operationType: "get_team_name",
        data: { team_id: teamId },
      };

      const response = await axios.post(`${API_URL}`, requestPayload);
      const responseData = response.data.body
        ? JSON.parse(response.data.body)
        : {};

      devLog(`Fetched team name for team ${teamId}:`, responseData.team_name);
      return responseData.team_name || "Unknown Team";
    } catch (error) {
      console.error(`Error fetching team name for team ${teamId}:`, error);
      return "Unknown Team";
    }
  };

  const fetchPlayers = async (teamId) => {
    devLog("🔄 [fetchPlayers] Starting to fetch players for team:", teamId);
    setLoading(true);

    try {
      // Fetch active players
      const activePlayersPayload = {
        operationType: "fetch_detailed_players_by_team",
        data: { team_id: teamId },
      };

      devLog(
        "📤 [fetchPlayers] Active players request payload:",
        activePlayersPayload
      );
      const activeResponse = await axios.post(
        `${API_URL}`,
        activePlayersPayload
      );
      devLog(
        "📥 [fetchPlayers] Active players raw response:",
        activeResponse.data
      );

      const allPlayersData = activeResponse.data.body
        ? JSON.parse(activeResponse.data.body).message
        : [];
      devLog(
        "📊 [fetchPlayers] All players data from active endpoint:",
        allPlayersData
      );

      // Filter active players (SK starts with PLAYER#, not PLAYER_ARCHIVE#)
      const activePlayersData = allPlayersData.filter((player) => {
        const isActive =
          player.SK &&
          player.SK.startsWith("PLAYER#") &&
          !player.SK.startsWith("PLAYER_ARCHIVE#");
        devLog(
          `🎯 [fetchPlayers] Player ${player.player_name} (SK: ${player.SK}): isActive=${isActive}`
        );
        return isActive;
      });

      devLog("✅ [fetchPlayers] Active players:", activePlayersData.length);
      setPlayers(activePlayersData);

      // Fetch archived players separately
      await fetchArchivedPlayers(teamId);
    } catch (error) {
      console.error("💥 [fetchPlayers] Error fetching active players:", error);
      setError("Error fetching players.");
      setPlayers([]);
      setArchivedPlayers([]);
    } finally {
      setLoading(false);
      devLog("🏁 [fetchPlayers] Finished fetching players");
    }
  };

  const fetchArchivedPlayers = async (teamId) => {
    devLog(
      "🔄 [fetchArchivedPlayers] Starting to fetch archived players for team:",
      teamId
    );

    try {
      // Fetch archived players using dedicated endpoint
      const archivedPlayersPayload = {
        operationType: "fetch_archived_players_by_team",
        data: { team_id: teamId },
      };

      devLog(
        "📤 [fetchArchivedPlayers] Archived players request payload:",
        archivedPlayersPayload
      );
      const archivedResponse = await axios.post(
        `${API_URL}`,
        archivedPlayersPayload
      );
      devLog(
        "📥 [fetchArchivedPlayers] Archived players raw response:",
        archivedResponse.data
      );

      const archivedPlayersData = archivedResponse.data.body
        ? JSON.parse(archivedResponse.data.body).message
        : [];
      devLog(
        "📊 [fetchArchivedPlayers] Archived players data:",
        archivedPlayersData
      );

      devLog(
        "✅ [fetchArchivedPlayers] Archived players:",
        archivedPlayersData.length
      );
      setArchivedPlayers(archivedPlayersData);
    } catch (error) {
      console.error(
        "💥 [fetchArchivedPlayers] Error fetching archived players:",
        error
      );
      // Don't show error for archived players - they're optional
      setArchivedPlayers([]);
    }

    devLog("🏁 [fetchArchivedPlayers] Finished fetching archived players");
  };

  const handleAddPlayer = async () => {
    if (newPlayerName.trim() === "") return;

    const cleanTeamId = selectedTeam.replace("TEAM#", "");

    const newPlayer = {
      team_id: cleanTeamId,
      player_name: newPlayerName,
      player_position: "Unknown",
      Physical: null,
      Physiological: null,
      Social: null,
      Tactical: null,
      Technical: null,
    };

    devLog("Adding new player:", newPlayer);
    setLoading(true);

    try {
      const requestPayload = {
        operationType: "create_new_player",
        data: newPlayer,
      };

      const response = await axios.post(`${API_URL}`, requestPayload);

      if (response.status === 200) {
        const responseBody =
          typeof response.data.body === "string"
            ? JSON.parse(response.data.body)
            : response.data.body;

        if (responseBody.player_id) {
          devLog("Player ID received from backend:", responseBody.player_id);
          setNewPlayerName("");
          setSuccessMessage("Player added successfully!");
          await fetchPlayers(selectedTeam); // This now handles both active and archived
        } else {
          console.error("No player ID returned from the backend.");
          setError("Failed to receive player ID from the server.");
        }
      } else {
        console.error("Error adding player:", response.statusText);
        setError("Failed to add player. Please try again.");
      }
    } catch (error) {
      console.error("Error adding player:", error);
      setError("Error adding player. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  // 🆕 ARCHIVE PLAYER - Using dedicated archive_player function
  const handleArchivePlayer = async (playerId) => {
    console.log(
      "🗃️ [handleArchivePlayer] Starting archive process for player:",
      playerId
    );

    if (
      !window.confirm(
        "Archive this player? They will be removed from all team activities but can be unarchived later."
      )
    ) {
      console.log("❌ [handleArchivePlayer] User cancelled archive");
      return;
    }

    console.log("✅ [handleArchivePlayer] User confirmed archive");
    setLoading(true);

    try {
      const cleanTeamId = selectedTeam.replace("TEAM#", "");
      const cleanPlayerId = playerId.replace("PLAYER#", "");

      console.log("🔧 [handleArchivePlayer] Processing IDs:", {
        originalTeamId: selectedTeam,
        cleanTeamId,
        originalPlayerId: playerId,
        cleanPlayerId,
      });

      // Use the dedicated archive_player function
      console.log(
        "🔄 [handleArchivePlayer] Using dedicated archive_player function..."
      );
      const archivePayload = {
        operationType: "archive_player",
        data: {
          team_id: cleanTeamId,
          player_id: cleanPlayerId, // This preserves the original player ID
        },
      };

      console.log("📤 [handleArchivePlayer] Archive payload:", archivePayload);
      const response = await axios.post(API_URL, archivePayload);
      console.log("📥 [handleArchivePlayer] Archive response:", response.data);

      if (response.status === 200) {
        console.log("✅ [handleArchivePlayer] Archive process successful");
        setSuccessMessage(
          "Player archived successfully with original ID preserved!"
        );
        console.log("🔄 [handleArchivePlayer] Reloading players...");
        await fetchPlayers(selectedTeam); // This now handles both active and archived
        console.log("✅ [handleArchivePlayer] Players reloaded");
      } else {
        throw new Error("Failed to archive player");
      }
    } catch (error) {
      console.error("💥 [handleArchivePlayer] Error archiving player:", error);
      setError("Error archiving player: " + error.message);
    } finally {
      setLoading(false);
      console.log("🏁 [handleArchivePlayer] Archive process completed");
    }
  };

  // 🆕 UNARCHIVE PLAYER - Using dedicated unarchive_player function
  const handleUnarchivePlayer = async (playerId) => {
    console.log(
      "🔄 [handleUnarchivePlayer] Starting unarchive process for player:",
      playerId
    );

    if (
      !window.confirm(
        "Unarchive this player? They will be restored to active status."
      )
    ) {
      console.log("❌ [handleUnarchivePlayer] User cancelled unarchive");
      return;
    }

    console.log("✅ [handleUnarchivePlayer] User confirmed unarchive");
    setLoading(true);

    try {
      const cleanTeamId = selectedTeam.replace("TEAM#", "");
      const cleanPlayerId = playerId.replace("PLAYER_ARCHIVE#", "");

      console.log("🔧 [handleUnarchivePlayer] Processing IDs:", {
        originalTeamId: selectedTeam,
        cleanTeamId,
        originalPlayerId: playerId,
        cleanPlayerId,
      });

      // Use the dedicated unarchive_player function
      console.log(
        "🔄 [handleUnarchivePlayer] Using dedicated unarchive_player function..."
      );
      const unarchivePayload = {
        operationType: "unarchive_player",
        data: {
          team_id: cleanTeamId,
          player_id: cleanPlayerId, // This preserves the original player ID
        },
      };

      console.log(
        "📤 [handleUnarchivePlayer] Unarchive payload:",
        unarchivePayload
      );
      const response = await axios.post(API_URL, unarchivePayload);
      console.log(
        "📥 [handleUnarchivePlayer] Unarchive response:",
        response.data
      );

      if (response.status === 200) {
        console.log("✅ [handleUnarchivePlayer] Unarchive process successful");
        setSuccessMessage(
          "Player unarchived successfully with original ID preserved!"
        );
        console.log("🔄 [handleUnarchivePlayer] Reloading players...");
        await fetchPlayers(selectedTeam); // This now handles both active and archived
        console.log("✅ [handleUnarchivePlayer] Players reloaded");
      } else {
        throw new Error("Failed to unarchive player");
      }
    } catch (error) {
      console.error(
        "💥 [handleUnarchivePlayer] Error unarchiving player:",
        error
      );
      setError("Error unarchiving player: " + error.message);
    } finally {
      setLoading(false);
      console.log("🏁 [handleUnarchivePlayer] Unarchive process completed");
    }
  };

  const handleDeletePlayer = async (playerId, isArchived = false) => {
    const playerType = isArchived ? "archived player" : "player";

    if (
      !window.confirm(
        `Are you sure you want to permanently delete this ${playerType}? This action cannot be undone.`
      )
    ) {
      return;
    }

    devLog("Trying to delete player with ID:", playerId);
    setLoading(true);

    const cleanTeamId = selectedTeam.replace("TEAM#", "");
    let cleanPlayerId, operationType;

    if (isArchived) {
      cleanPlayerId = playerId.replace("PLAYER_ARCHIVE#", "");
      operationType = "delete_archived_player"; // Use your specialized function
    } else {
      cleanPlayerId = playerId.replace("PLAYER#", "");
      operationType = "delete_detailed_player"; // Use regular delete
    }

    try {
      const requestPayload = {
        operationType: operationType,
        data: {
          team_id: cleanTeamId,
          player_id: cleanPlayerId,
        },
      };

      devLog("Request payload for delete operation:", requestPayload);

      const response = await axios.post(`${API_URL}`, requestPayload);
      devLog("Response from handleDeletePlayer:", response.data);

      if (response.status === 200) {
        setSuccessMessage(`${playerType} deleted successfully!`);
        await fetchPlayers(selectedTeam); // This now handles both active and archived
        setConfirmDeletePlayer(null);
      } else {
        console.error("Error deleting player:", response.statusText);
        setError("Failed to delete player. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting player:", error);
      setError("Error deleting player.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAndSavePlayer = async (playerId, isArchived = false) => {
    if (editingPlayer === playerId) {
      if (editedPlayerName.trim() === "") {
        alert("Player name cannot be empty.");
        return;
      }

      const playerToEdit = isArchived
        ? archivedPlayers.find((player) => player.SK === playerId)
        : players.find((player) => player.SK === playerId);

      if (!playerToEdit) {
        console.error(`Player with ID ${playerId} not found.`);
        return;
      }

      const cleanTeamId = selectedTeam.replace("TEAM#", "");
      let cleanPlayerId;

      if (isArchived) {
        cleanPlayerId = playerId.replace("PLAYER_ARCHIVE#", "");
      } else {
        cleanPlayerId = playerId.replace("PLAYER#", "");
      }

      if (!cleanTeamId || !cleanPlayerId) {
        console.error("Invalid team or player ID.");
        return;
      }

      setLoading(true);

      try {
        const requestPayload = {
          operationType: "edit_player",
          data: {
            team_id: cleanTeamId,
            player_id: cleanPlayerId,
            updated_fields: {
              player_name: editedPlayerName,
            },
          },
        };

        devLog("Editing player with payload:", requestPayload);

        const response = await axios.post(`${API_URL}`, requestPayload);

        if (response.status === 200) {
          devLog("Player edited successfully!");
          setSuccessMessage(`Player ${editedPlayerName} updated successfully!`);
          setEditedPlayerName("");
          setEditingPlayer(null);
          await fetchPlayers(selectedTeam); // This now handles both active and archived
        } else {
          console.error("Error editing player:", response.statusText);
          setError("Failed to edit player. Please try again.");
        }
      } catch (error) {
        console.error("Error editing player:", error);
        setError("Error editing player.");
      } finally {
        setLoading(false);
      }
    } else {
      const playerToEdit = isArchived
        ? archivedPlayers.find((player) => player.SK === playerId)
        : players.find((player) => player.SK === playerId);

      if (!playerToEdit) {
        console.error(`Player with ID ${playerId} not found.`);
        return;
      }

      setEditedPlayerName(playerToEdit.player_name);
      setEditingPlayer(playerId);
    }
  };

  const handleConfirmDeletePlayer = (playerId) => {
    setConfirmDeletePlayer(playerId);
  };

  return (
    <div className="manage-team-container">
      <div className="logo-container">
        <img src={logo} alt="KICKS Logo" className="logo-manage-team" />
      </div>
      <h2 className="manage-team-title">Manage Your Team</h2>

      {/* Loading Spinner */}
      {loading && (
        <div className="spinner-container">
          <div className="football-spinner"></div>
        </div>
      )}

      {/* Messages */}
      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {/* Team Selection */}
      {teams.length > 1 && (
        <div className="dropdown-section">
          <label>My Teams:</label>
          <select
            value={selectedTeam}
            onChange={(e) => {
              setSelectedTeam(e.target.value);
            }}
            className="dropdown-select"
          >
            <option value="">Select a team</option>
            {teams.map((team) => (
              <option key={team.team_id} value={team.team_id}>
                {team.team_name || team.team_id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Toggle Button for Preferences */}
      <button
        className="toggle-preferences-button"
        onClick={() => setPreferencesVisible(!preferencesVisible)}
      >
        <FontAwesomeIcon
          icon={preferencesVisible ? faChevronUp : faChevronDown}
          style={{ marginRight: "8px" }}
        />
        {preferencesVisible
          ? "Hide Match Preferences"
          : "Show Match Preferences"}
      </button>

      {/* Team Preferences */}
      {preferencesVisible && (
        <div className="team-preferences-section">
          <h3>Team Preferences</h3>

          {/* Match Type Dropdown */}
          <div className="preferences-group">
            <label htmlFor="matchType" className="preferences-label">
              Match Type
            </label>
            <select
              id="matchType"
              value={matchType}
              onChange={(e) => {
                setMatchType(e.target.value);
                handlePreferenceChange("matchType", e.target.value);
              }}
              className="preferences-dropdown"
            >
              {matchTypes.map((type) => (
                <option key={type} value={type}>
                  {type}v{type}
                </option>
              ))}
            </select>
          </div>

          {/* Match Length Dropdown */}
          <div className="preferences-group">
            <label htmlFor="matchLength" className="preferences-label">
              Part Length
            </label>
            <select
              id="matchLength"
              value={matchLength}
              onChange={(e) => {
                setMatchLength(e.target.value);
                handlePreferenceChange("matchLength", e.target.value);
              }}
              className="preferences-dropdown"
            >
              {matchLengths.map((length) => (
                <option key={length} value={length}>
                  {length} mins
                </option>
              ))}
            </select>
          </div>

          {/* Number of Parts Dropdown */}
          <div className="preferences-group">
            <label htmlFor="numberOfParts" className="preferences-label">
              Parts
            </label>
            <select
              id="numberOfParts"
              value={numberOfParts}
              onChange={(e) => {
                setNumberOfParts(e.target.value);
                handlePreferenceChange("numberOfParts", e.target.value);
              }}
              className="preferences-dropdown"
            >
              <option value="2">2 Halves</option>
              <option value="4">4 Quarters</option>
            </select>
          </div>

          {/* Marker Color Selector */}
          <div className="preferences-group">
            <label htmlFor="markerColor" className="preferences-label">
              Player Marker Color
            </label>
            <select
              id="markerColor"
              value={markerColor}
              onChange={(e) => {
                setMarkerColor(e.target.value);
                handlePreferenceChange("markerColor", e.target.value);
              }}
              className="preferences-dropdown"
            >
              {colorOptions.map((color) => (
                <option key={color.value} value={color.value}>
                  {color.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Active Players Section */}
      {selectedTeam && (
        <>
          {players.length > 0 ? (
            <div className="players-section">
              <h3>
                Active Players in{" "}
                {teams.find((team) => team.team_id === selectedTeam)
                  ?.team_name || "Team"}
              </h3>
              <ul>
                {players.map((player) =>
                  player && player.SK ? (
                    <li key={player.SK} className="player-item">
                      {editingPlayer === player.SK ? (
                        <div className="edit-player-container">
                          <input
                            type="text"
                            value={editedPlayerName}
                            onChange={(e) =>
                              setEditedPlayerName(e.target.value)
                            }
                            placeholder="Enter player name"
                            className="edit-player-input"
                          />
                          <button
                            className="save-player-button"
                            onClick={() => handleEditAndSavePlayer(player.SK)}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                          <button
                            className="cancel-edit-button"
                            onClick={() => {
                              setEditingPlayer(null);
                              setEditedPlayerName("");
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ) : confirmDeletePlayer === player.SK ? (
                        <div className="delete-confirmation-container confirmation-pill">
                          <button
                            className="confirm-delete-icon"
                            onClick={() => {
                              handleDeletePlayer(player.SK);
                            }}
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </button>
                          <button
                            className="cancel-delete-icon"
                            onClick={() => setConfirmDeletePlayer(null)}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      ) : (
                        <div className="player-item-content">
                          <span className="player-name">
                            {player.player_name}
                          </span>
                          <div className="player-actions">
                            <button
                              className="edit-player-button"
                              onClick={() => handleEditAndSavePlayer(player.SK)}
                              title="Edit player"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              className="archive-player-button"
                              onClick={() => handleArchivePlayer(player.SK)}
                              title="Archive player"
                            >
                              <FontAwesomeIcon icon={faArchive} />
                            </button>
                            <button
                              className="delete-player-button"
                              onClick={() =>
                                handleConfirmDeletePlayer(player.SK)
                              }
                              title="Delete player permanently"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  ) : (
                    <li key={uuidv4()} className="player-item">
                      Invalid player data
                    </li>
                  )
                )}
              </ul>
            </div>
          ) : (
            <p className="no-players-text">
              No active players found for this team.
            </p>
          )}

          {/* Archived Players Toggle */}
          <button
            className="toggle-archived-button"
            onClick={() => setShowArchivedPlayers(!showArchivedPlayers)}
          >
            <FontAwesomeIcon
              icon={showArchivedPlayers ? faChevronUp : faChevronDown}
              style={{ marginRight: "8px" }}
            />
            {showArchivedPlayers
              ? "Hide Archived Players"
              : `Show Archived Players${
                  archivedPlayers.length > 0
                    ? ` (${archivedPlayers.length})`
                    : ""
                }`}
          </button>

          {/* Archived Players Section */}
          {showArchivedPlayers && (
            <div className="archived-players-section">
              <h3>Archived Players</h3>
              {archivedPlayers.length > 0 ? (
                <ul>
                  {archivedPlayers.map((player) =>
                    player && player.SK ? (
                      <li
                        key={player.SK}
                        className="player-item archived-player-item"
                      >
                        {/* Remove all edit mode logic - only show normal view or delete confirmation */}
                        {confirmDeletePlayer === player.SK ? (
                          <div className="delete-confirmation-container confirmation-pill">
                            <button
                              className="confirm-delete-icon"
                              onClick={() => {
                                handleDeletePlayer(player.SK, true);
                              }}
                            >
                              <FontAwesomeIcon icon={faCheck} />
                            </button>
                            <span>Are you sure?</span>
                            <button
                              className="cancel-delete-icon"
                              onClick={() => setConfirmDeletePlayer(null)}
                            >
                              <FontAwesomeIcon icon={faTimes} />
                            </button>
                          </div>
                        ) : (
                          <div className="player-item-content">
                            <span className="player-name archived-player-name">
                              {player.player_name}
                            </span>
                            <div className="player-actions">
                              {/* Remove edit button - only keep unarchive and delete */}
                              <button
                                className="unarchive-player-button"
                                onClick={() => handleUnarchivePlayer(player.SK)}
                                title="Unarchive player"
                              >
                                <FontAwesomeIcon icon={faUndo} />
                              </button>
                              <button
                                className="delete-player-button"
                                onClick={() =>
                                  handleConfirmDeletePlayer(player.SK)
                                }
                                title="Delete archived player permanently"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    ) : (
                      <li key={uuidv4()} className="player-item">
                        Invalid player data
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p className="no-players-text">No archived players.</p>
              )}
            </div>
          )}

          {/* Add Player Section */}
          <div className="add-player-section">
            <input
              type="text"
              value={newPlayerName}
              onChange={(e) => setNewPlayerName(e.target.value)}
              placeholder="Enter player name"
              className="add-player-input"
            />
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={handleAddPlayer} className="add-player-button">
                Add Player
              </button>
              <button
                onClick={() => navigate("/")}
                className="add-player-button"
              >
                Home
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ManageTeamPage;
